import React, { useState, useMemo } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { Badge } from "@shared/ui/badge";
import { Check } from "lucide-react";
import {
  AssetClassOption,
  ChoiceOption,
  GroupedValue,
  ManufacturerOption,
  TerritoryOption,
  UtilityServiceAXOption,
} from "@features/onboarding/data-migration/types";
import { DataRelationFilter } from "../filter/DataRelationshipFilter";
import { useLocation } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import {
  useCategoryOptions,
  usePlanOptions,
  useConsumerStatusOptions,
  useMeterTypeOptions,
  useMeterManufacturerOptions,
  useMeterModelOptions,
  useUtilityServicesOptions,
  useGroupedRelationData,
  useAssetClassOptions,
  useAssetTypeOptions,
  useBackupStatusOptions,
  useDepreciationMethodOptions,
  useDepreciationScheduleOptions,
  useFacilityStatusOptions,
  useFacilityTypeOptions,
  useManufacturerAXOptions,
  useNetworkTypeOptions,
  useOperationalHoursOptions,
  useSystemTypeOptions,
  useTerritoryZoneOptions,
  useUtilityServicesAXOptions,
  useManagers,
  usePremiseOptions,
  useOperationalStatusOptions,
  useSystemStatusOptions,
} from "../../../hooks";

interface GroupedDataTableProps {
  sessionId: string;
  dataType: string;
  tabName: string;
  targetField: string;
  onAssign: (
    sourceValue: string,
    targetId: number | string,
    targetName: string
  ) => void;
  isAssigning: boolean;
  assignmentState: Record<string, Assignment | null>;
  onAssignmentChange: (assignments: Record<string, Assignment>) => void;
}
interface Assignment {
  targetId: number | string;
  targetName: string;
  timestamp: number;
}

export const GroupedDataTable: React.FC<GroupedDataTableProps> = ({
  sessionId,
  dataType,
  tabName,
  targetField,
  onAssign,
  isAssigning,
  assignmentState, // ✅ ADD: Receive from parent
  onAssignmentChange,
}) => {
  const [selectedValues, setSelectedValues] = useState<Record<string, string>>(
    {}
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const urlTab = searchParams.get("tab") || tabName;
  const searchTerm = searchParams.get("search") || "";
  const filterStatus = (searchParams.get("status") || "all") as
    | "all"
    | "mapped"
    | "unmapped";

  // Format filters for the hook
  const currentFilters = useMemo(() => {
    const filters: Record<string, any> = {};
    searchParams.forEach((value, key) => {
      if (key.startsWith("filter_")) {
        const filterKey = key.replace("filter_", "");
        filters[filterKey] = value;
      }
    });
    return {
      search: searchTerm,
      status: filterStatus,
      ...filters,
    };
  }, [searchParams, searchTerm, filterStatus]);

  // ✅ Fetch grouped data with correct parameter order
  const { data: groupedData, isLoading } = useGroupedRelationData(
    sessionId,
    tabName,
    currentFilters,
    true
  );

  const groupedValues = groupedData?.groupedValues || [];

  const enrichedGroupedValues = useMemo(() => {
    if (!groupedValues || groupedValues.length === 0) {
      return [];
    }

    return groupedValues.map((group) => {
      const assignment = assignmentState[group.sourceValue];

      // ✅ If there's a local assignment, use it
      if (assignment) {
        return {
          ...group,
          assignedTarget: {
            targetId: assignment.targetId,
            targetName: assignment.targetName,
          },
        };
      }

      // ✅ If assignment is explicitly null, hide API data (undone state)
      if (assignment === null) {
        return {
          ...group,
          assignedTarget: null,
        };
      }

      // ✅ Otherwise, use API data
      return group;
    });
  }, [groupedValues, assignmentState]);

  const { data: planData, isLoading: isPlanLoading } = usePlanOptions(
    urlTab === "plan"
  );
  const { data: categoryData, isLoading: isCategoryLoading } =
    useCategoryOptions(
      urlTab,
      urlTab === "category" || urlTab === "sub_category"
    );

  const { data: statusData, isLoading: isStatusLoading } =
    dataType == "Systems"
      ? useSystemStatusOptions(urlTab === "status")
      : useConsumerStatusOptions(urlTab === "status");

  const { data: meterTypeData, isLoading: isMeterTypeLoading } =
    useMeterTypeOptions(urlTab === "meter_type");
  const { data: manufacturerData, isLoading: isManufacturerLoading } =
    useMeterManufacturerOptions(urlTab === "meter_manufacturer");
  const { data: meterModelData, isLoading: isMeterModelLoading } =
    useMeterModelOptions(urlTab === "meter_model");
  const { data: utilityServicesData, isLoading: isUtilityServicesLoading } =
    useUtilityServicesOptions(urlTab === "utility_service");
  // ✅ ADD: Asset Management hooks
  const { data: assetClassData, isLoading: isAssetClassLoading } =
    useAssetClassOptions(urlTab === "asset_class");

  const { data: assetTypeData, isLoading: isAssetTypeLoading } =
    useAssetTypeOptions(urlTab === "asset_type");
  const { data: manufacturerAXData, isLoading: isManufacturerAXLoading } =
    useManufacturerAXOptions(urlTab === "meter_manufacturer", 1, 1000);
  const {
    data: depreciationMethodData,
    isLoading: isDepreciationMethodLoading,
  } = useDepreciationMethodOptions(urlTab === "depreciation_method");
  const { data: networkTypeData, isLoading: isNetworkTypeLoading } =
    useNetworkTypeOptions(urlTab === "network_type");
  const { data: facilityStatusData, isLoading: isFacilityStatusLoading } =
    useFacilityStatusOptions(
      urlTab === "operating_status" || urlTab === "facility_status"
    );
  const { data: backupStatusData, isLoading: isBackupStatusLoading } =
    useBackupStatusOptions(urlTab === "backup_status");
  const { data: systemTypeData, isLoading: isSystemTypeLoading } =
    useSystemTypeOptions(urlTab === "system_type");
  const { data: operationalHoursData, isLoading: isOperationalHoursLoading } =
    useOperationalHoursOptions(urlTab === "operational_hours");
  const { data: operationalStatusData, isLoading: isOperationalStatusLoading } =
    useOperationalStatusOptions(urlTab === "operational_status");
  const { data: facilityTypeData, isLoading: isFacilityTypeLoading } =
    useFacilityTypeOptions(urlTab === "facility_type");
  const { data: utilityServicesAXData, isLoading: isUtilityServicesAXLoading } =
    useUtilityServicesAXOptions(urlTab === "utility_service_ax");
  const { data: territoryZoneData, isLoading: isTerritoryZoneLoading } =
    useTerritoryZoneOptions(urlTab === "service_zone_division");
  const {
    data: depreciationScheduleData,
    isLoading: isDepreciationScheduleLoading,
  } = useDepreciationScheduleOptions(urlTab === "depreciation_schedule");
  const { data: facilityManager, isLoading: isFacilityManagerLoading } =
    useManagers(urlTab === "network_operator");

  const { data: premiseData, isLoading: isPremiseLoading } = usePremiseOptions(
    urlTab === "billing_premise" || urlTab === "service_premise"
  );

  // ✅ Then select the appropriate data based on urlTab
  const { optionsData, isOptionsLoading } = useMemo(() => {
    switch (urlTab) {
      case "plan":
        return { optionsData: planData || [], isOptionsLoading: isPlanLoading };
      case "category":
      case "sub_category":
        return {
          optionsData: categoryData || [],
          isOptionsLoading: isCategoryLoading,
        };
      case "status":
        return {
          optionsData: statusData || [],
          isOptionsLoading: isStatusLoading,
        };
      case "meter_type":
        return {
          optionsData: meterTypeData || [],
          isOptionsLoading: isMeterTypeLoading,
        };
      case "meter_manufacturer":
        return {
          optionsData: manufacturerData || [],
          isOptionsLoading: isManufacturerLoading,
        };
      case "meter_model":
        return {
          optionsData: meterModelData || [],
          isOptionsLoading: isMeterModelLoading,
        };
      case "utility_service":
        return {
          optionsData: utilityServicesData || [],
          isOptionsLoading: isUtilityServicesLoading,
        };

      // ✅ Asset Management cases
      case "asset_class":
        return {
          optionsData: assetClassData || [],
          isOptionsLoading: isAssetClassLoading,
        };
      case "asset_type":
        return {
          optionsData: assetTypeData || [],
          isOptionsLoading: isAssetTypeLoading,
        };
      case "manufacturer":
        return {
          optionsData: manufacturerAXData || [],
          isOptionsLoading: isManufacturerAXLoading,
        };
      case "depreciation_method":
        return {
          optionsData: depreciationMethodData || [],
          isOptionsLoading: isDepreciationMethodLoading,
        };
      case "network_type":
        return {
          optionsData: networkTypeData || [],
          isOptionsLoading: isNetworkTypeLoading,
        };
      case "operating_status":
      case "facility_status":
        return {
          optionsData: facilityStatusData || [],
          isOptionsLoading: isFacilityStatusLoading,
        };
      case "backup_status":
        return {
          optionsData: backupStatusData || [],
          isOptionsLoading: isBackupStatusLoading,
        };
      case "system_type":
        return {
          optionsData: systemTypeData || [],
          isOptionsLoading: isSystemTypeLoading,
        };
      case "operational_hours":
        return {
          optionsData: operationalHoursData || [],
          isOptionsLoading: isOperationalHoursLoading,
        };
      case "operational_status":
        return {
          optionsData: operationalStatusData || [],
          isOptionsLoading: isOperationalStatusLoading,
        };
      case "facility_type":
        return {
          optionsData: facilityTypeData || [],
          isOptionsLoading: isFacilityTypeLoading,
        };
      case "utility_service_ax":
        return {
          optionsData: utilityServicesAXData || [],
          isOptionsLoading: isUtilityServicesAXLoading,
        };
      case "service_zone_division":
        return {
          optionsData: territoryZoneData || [],
          isOptionsLoading: isTerritoryZoneLoading,
        };
      case "depreciation_schedule":
        return {
          optionsData: depreciationScheduleData || [],
          isOptionsLoading: isDepreciationScheduleLoading,
        };
      case "network_operator":
        return {
          optionsData: facilityManager || [],
          isOptionsLoading: isFacilityManagerLoading,
        };
      case "billing_premise":
      case "service_premise":
        return {
          optionsData: premiseData?.result || [],
          isOptionsLoading: isPremiseLoading,
        };
      case "data_type":
        return {
          optionsData: [
            { key: "text", value: "Text" },
            { key: "number", value: "Number" },
            { key: "dropdown", value: "Dropdown" },
            { key: "date", value: "Date" },
          ],
          isOptionsLoading: false,
        };

      default:
        return { optionsData: [], isOptionsLoading: false };
    }
  }, [
    urlTab,
    planData,
    categoryData,
    statusData,
    meterTypeData,
    manufacturerData,
    meterModelData,
    utilityServicesData,
    assetClassData,
    assetTypeData,
    manufacturerAXData,
    depreciationMethodData,
    networkTypeData,
    facilityStatusData,
    backupStatusData,
    systemTypeData,
    operationalHoursData,
    facilityTypeData,
    utilityServicesAXData,
    territoryZoneData,
    depreciationScheduleData,
    facilityManager,
    premiseData,
    isPlanLoading,
    isCategoryLoading,
    isStatusLoading,
    isMeterTypeLoading,
    isManufacturerLoading,
    isMeterModelLoading,
    isUtilityServicesLoading,
    isAssetClassLoading,
    isAssetTypeLoading,
    isManufacturerAXLoading,
    isDepreciationMethodLoading,
    isNetworkTypeLoading,
    isFacilityStatusLoading,
    isBackupStatusLoading,
    isSystemTypeLoading,
    isOperationalHoursLoading,
    isOperationalStatusLoading,
    isFacilityTypeLoading,
    isUtilityServicesAXLoading,
    isTerritoryZoneLoading,
    isDepreciationScheduleLoading,
    isFacilityManagerLoading,
    isPremiseLoading,
  ]);

  // Map dropdown options based on tab type
  const dropdownOptions = useMemo<{ value: string; label: string }[]>(() => {
    if (!optionsData) return [];

    switch (urlTab) {
      case "plan":
        return optionsData.map((p: any) => ({
          value: String(p.id),
          label: p.planName,
        }));
      case "category":
      case "sub_category":
        return optionsData.map((c: any) => ({
          value: c.name,
          label: c.name,
        }));
      case "status":
        return optionsData.map((s: any) => ({
          value: String(s.key),
          label: s.value,
        }));
      case "meter_type":
        return optionsData.map((t: any) => ({
          value: String(t.key),
          label: t.value,
        }));
      case "meter_model":
        return optionsData.map((mm: any) => ({
          value: String(mm.id),
          label: mm.meterModel,
        }));
      case "utility_service":
        return optionsData
          .filter((s: any) => s.isActive)
          .map((s: any) => ({
            value: String(s.id),
            label: s.name,
          }));
      case "asset_class":
      case "asset_type":
      case "system_type":
      case "facility_type":
        return optionsData.map((item: AssetClassOption) => ({
          value: item.code,
          label: item.name,
        }));

      case "manufacturer":
        return optionsData.map((m: ManufacturerOption) => ({
          value: String(m.id),
          label: m.name,
        }));

      case "depreciation_method":
      case "network_type":
      case "operating_status":
      case "facility_status":
      case "backup_status":
      case "operational_hours":
      case "operational_status":
      case "depreciation_schedule":
        return optionsData.map((choice: ChoiceOption) => ({
          value: String(choice.key),
          label: choice.value,
        }));

      case "utility_service_ax":
        return optionsData
          .filter((s: UtilityServiceAXOption) => s.isActive)
          .map((s: UtilityServiceAXOption) => ({
            value: String(s.id),
            label: s.name,
          }));

      case "service_zone_division":
        return optionsData?.map((zone: TerritoryOption) => ({
          value: zone.code,
          label: zone.name,
        }));
      case "operator_network":
        return (
          optionsData?.result?.map((fm: any) => ({
            value: String(fm.user.id),
            label: fm.user.name,
          })) || []
        );
      case "billing_premise":
      case "service_premise":
        return optionsData.map((premise: any) => ({
          value: premise.code,
          label: premise.name,
        }));

      case "data_type":
        return optionsData.map((item: any) => ({
          value: item.key,
          label: item.value,
        }));

      default:
        return [];
    }
  }, [urlTab, optionsData]);

  const handleAssign = (sourceValue: string) => {
    const selected = selectedValues[sourceValue];
    if (!selected) return;

    const option = dropdownOptions.find((opt) => opt.value === selected);
    if (!option) return;

    let targetId: number | string;

    if (urlTab === "meter_type" || urlTab === "status") {
      targetId = parseInt(option.value);
    } else if (
      urlTab === "utility_service" ||
      urlTab === "manufacturer" ||
      urlTab === "meter_model" ||
      urlTab === "plan"
    ) {
      targetId = parseInt(option.value);
    } else {
      targetId = option.value;
    }

    // ✅ UPDATE: Use callback from parent
    onAssignmentChange({
      ...assignmentState,
      [sourceValue]: {
        targetId: targetId,
        targetName: option.label,
        timestamp: Date.now(),
      },
    });

    onAssign(sourceValue, targetId, option.label);

    setSelectedValues((prev) => {
      const newState = { ...prev };
      delete newState[sourceValue];
      return newState;
    });
  };

  // Filter logic for search and status
  const filteredGroups = useMemo(() => {
    return enrichedGroupedValues.filter((group) => {
      const matchesSearch = group.sourceValue
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      const matchesStatus =
        filterStatus === "all" ||
        (filterStatus === "mapped" && group.assignedTarget) ||
        (filterStatus === "unmapped" && !group.assignedTarget);

      return matchesSearch && matchesStatus;
    });
  }, [enrichedGroupedValues, searchTerm, filterStatus]);

  const handleUndoAssignment = (sourceValue: string) => {
    // ✅ Set to null to mark as "undone" instead of deleting
    onAssignmentChange({
      ...assignmentState,
      [sourceValue]: null, // Mark as undone
    });
  };

  // Table columns definition
  const columns: TableColumn<any>[] = useMemo(
    () => [
      {
        key: "sourceValue",
        header: "Source Value",
        type: "custom",
        render: (value: any) => <Badge variant="outline">{value}</Badge>,
      },
      {
        key: "rowCount",
        header: "Row Count",
        type: "custom",
        render: (value: any) => (
          <span className="text-sm text-gray-600">{value} rows</span>
        ),
      },
      {
        key: "assignTo",
        header: "Assign To",
        type: "custom",
        render: (value: any, row: any) => {
          if (row.assignedTarget) {
            return (
              <Badge variant="default" className="bg-green-600">
                <Check className="h-3 w-3 mr-1" />
                {row.assignedTarget.name || row.assignedTarget.targetName}
              </Badge>
            );
          }

          return (
            <Select
              value={selectedValues[row.sourceValue] || ""}
              onValueChange={(val) =>
                setSelectedValues((prev) => ({
                  ...prev,
                  [row.sourceValue]: val,
                }))
              }
              disabled={isOptionsLoading}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue
                  placeholder={
                    isOptionsLoading ? "Loading options..." : "Select..."
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {dropdownOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        },
      },
      {
        key: "action",
        header: "Action",
        type: "custom",
        render: (value: any, row: any) => {
          // If assigned (from any source), show Undo button
          if (row.assignedTarget) {
            return (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleUndoAssignment(row.sourceValue)}
                className="border-yellow-500 text-yellow-600 hover:bg-orange-50 hover:text-orange-700"
              >
                Undo
              </Button>
            );
          }

          // If not assigned, show Assign button with dropdown
          return (
            <Button
              size="sm"
              onClick={() => handleAssign(row.sourceValue)}
              disabled={!selectedValues[row.sourceValue] || isAssigning}
            >
              {isAssigning ? "Assigning..." : "Assign"}
            </Button>
          );
        },
      },
    ],
    [
      selectedValues,
      dropdownOptions,
      isAssigning,
      isOptionsLoading,
      handleAssign,
    ]
  );

  // Table data mapping
  const tableData = useMemo(() => {
    return filteredGroups.map((group) => ({
      id: group.sourceValue,
      sourceValue: group.sourceValue,
      rowCount: group.rowCount,
      assignedTarget: group.assignedTarget,
    }));
  }, [filteredGroups]);

  const handleSearch = (searchValue: string, paramName: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (searchValue.trim()) {
      newParams.set(paramName, searchValue.trim());
      newParams.set("page", "1");
    } else {
      newParams.delete(paramName);
    }
    setSearchParams(newParams);
  };

  // ✅ ADD: Handle filter apply
  const handleApplyFilters = (newFilters: any) => {
    const newParams = new URLSearchParams(searchParams);

    // Clear existing filter params
    Array.from(newParams.keys()).forEach((key) => {
      if (key.startsWith("filter_")) {
        newParams.delete(key);
      }
    });

    // Add new filters
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value && value !== "all" && value !== "") {
        newParams.set(`filter_${key}`, String(value));
      }
    });

    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  // ✅ ADD: Handle filter reset
  const handleResetFilters = () => {
    const newParams = new URLSearchParams();
    const currentTabParam = searchParams.get("tab");

    if (currentTabParam) {
      newParams.set("tab", currentTabParam);
    }

    newParams.set("page", "1");
    setSearchParams(newParams);
  };
  const assignedCount = Object.keys(assignmentState).length;
  return (
    <div className="space-y-4">
      {assignedCount > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-green-700">
              <span className="font-medium">{assignedCount}</span> value
              {assignedCount !== 1 ? "s" : ""} assigned and saved
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAssignmentChange({})} // ✅ UPDATE: Use callback
            >
              Clear View
            </Button>
          </div>
        </div>
      )}
      {/* Summary header */}
      <div className="flex items-center justify-between text-sm text-gray-600 px-4 py-2 bg-gray-50 rounded-lg border">
        <span>
          Showing {filteredGroups.length} of {groupedValues.length} unique
          values
        </span>
        <span>
          {enrichedGroupedValues.filter((g) => g.assignedTarget).length} mapped,{" "}
          {enrichedGroupedValues.filter((g) => !g.assignedTarget).length}{" "}
          unmapped
        </span>
      </div>

      {/* Table */}
      <GenericTable
        data={tableData}
        columns={columns}
        rowKey="id"
        searchDebounceMs={300}
        searchPlaceholder="Search source values..."
        searchParamName="search"
        onSearch={handleSearch}
        searchValue={searchTerm}
        emptyMessage="No grouped values found"
      />
    </div>
  );
};
